import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { getLegalSourceFromLocation, LEGAL_SOURCE_SETTINGS } from "../auth/LegalPageShell";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";
import { isNativeApp } from "../../native/runtime";
import LandingInfoContent from "./components/LandingInfoContent";
import LandingInfoTabs, { isLandingInfoTabKey } from "./components/LandingInfoTabs";
import LandingHeader from "./sections/components/LandingHeader";

const HOST_RULES_RETURN_FLOW = "host_create_rules";
const JOIN_RULES_RETURN_FLOW = "event_join_rules";
const CHAT_HOST_RULES_RETURN_FLOW = "chat_host_rules";
const TAB_TRANSITION_MS = 180;

export default function AboutPage() {
  const { t, i18n } = useTranslation("about");
  const { t: tLanding } = useTranslation("landing");
  const location = useLocation();
  const navigate = useNavigate();
  const tabTransitionRef = useRef(null);
  const isNative = isNativeApp();

  const [activeKey, setActiveKey] = useState("about");
  const [displayKey, setDisplayKey] = useState("about");
  const [isContentVisible, setIsContentVisible] = useState(true);
  const isTapRulesPath = location.pathname === "/about/tap-rules";

  const searchParams = new URLSearchParams(location.search);
  const returnFlow = searchParams.get("return_flow") || "";
  const rawReturnTo = searchParams.get("return_to") || "";
  const parsedReturnCard = Number(searchParams.get("return_card"));
  const safeReturnCard = Number.isFinite(parsedReturnCard)
    ? Math.max(0, Math.min(5, Math.floor(parsedReturnCard)))
    : 0;
  const safeReturnTo =
    rawReturnTo && rawReturnTo.startsWith("/") ? rawReturnTo : "/events/create";
  const showReturnToRulesSource =
    [HOST_RULES_RETURN_FLOW, JOIN_RULES_RETURN_FLOW, CHAT_HOST_RULES_RETURN_FLOW].includes(returnFlow) &&
    Boolean(rawReturnTo);
  const isSkLang = String(i18n.language || "").toLowerCase().startsWith("sk");
  const isCsLang = String(i18n.language || "").toLowerCase().startsWith("cs");
  const isRuLang = String(i18n.language || "").toLowerCase().startsWith("ru");
  const showSettingsReturn = getLegalSourceFromLocation(location, "") === LEGAL_SOURCE_SETTINGS;
  const showDesktopLandingHeader = !isNative && !showReturnToRulesSource && !showSettingsReturn;
  const contentTopPaddingClass = showReturnToRulesSource
    ? "pt-[calc(176px+var(--vg-safe-area-top))] md:pt-36"
    : showDesktopLandingHeader
      ? "pt-[calc(128px+var(--vg-safe-area-top))] md:pt-56"
      : "pt-[calc(128px+var(--vg-safe-area-top))] md:pt-36";
  const backToRulesSourceLabel = returnFlow === CHAT_HOST_RULES_RETURN_FLOW
    ? isCsLang
      ? "Zpět do chatu"
    : isSkLang
      ? "Späť do chatu"
      : isRuLang
        ? "Вернуться в чат"
        : "Back to chat"
    : returnFlow === JOIN_RULES_RETURN_FLOW
      ? isCsLang
        ? "Zpět k připojení"
      : isSkLang
        ? "Späť k pripojeniu"
        : isRuLang
          ? "Вернуться к присоединению"
          : "Back to joining"
      : isCsLang
        ? "Zpět k vytváření události"
      : isSkLang
        ? "Späť k vytváraniu eventu"
        : isRuLang
          ? "Вернуться к созданию ивента"
          : "Back to event creation";
  const backToSettingsLabel = t("navigation.back_to_settings");
  const bottomBackLabel = showReturnToRulesSource
    ? backToRulesSourceLabel
    : showSettingsReturn
      ? backToSettingsLabel
      : isNative
        ? t("navigation.back")
        : tLanding("section1.auth.back_to_main");

  const handleReturnToRulesSource = () => {
    navigate(safeReturnTo, {
      replace: false,
      state: {
        ...(returnFlow === JOIN_RULES_RETURN_FLOW
          ? {
              resumeJoinRulesModal: true,
              resumeJoinRulesCard: safeReturnCard,
            }
          : {
              resumeHostCreateRulesModal: true,
              resumeHostCreateRulesCard: safeReturnCard,
            }),
      },
    });
  };

  const handleBottomBack = () => {
    if (showReturnToRulesSource) {
      handleReturnToRulesSource();
      return;
    }
    if (showSettingsReturn) {
      navigate("/vault/settings");
      return;
    }
    if (isNative) {
      const historyIndex = typeof window !== "undefined"
        ? Number(window.history.state?.idx ?? 0)
        : 0;
      if (historyIndex > 0) {
        navigate(-1);
      } else {
        navigate("/vault/settings", { replace: true });
      }
      return;
    }
    navigate("/");
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");

    if (tab && isLandingInfoTabKey(tab)) {
      setActiveKey(tab);
      return;
    }
    if (isTapRulesPath) {
      setActiveKey("rules");
      return;
    }
    if (!tab) {
      setActiveKey("about");
    }
  }, [isTapRulesPath, location.search]);

  useEffect(() => {
    if (tabTransitionRef.current) {
      window.clearTimeout(tabTransitionRef.current);
    }

    if (displayKey === activeKey) {
      setIsContentVisible(true);
      return undefined;
    }

    setIsContentVisible(false);
    tabTransitionRef.current = window.setTimeout(() => {
      setDisplayKey(activeKey);
      setIsContentVisible(true);
    }, TAB_TRANSITION_MS);

    return () => {
      if (tabTransitionRef.current) {
        window.clearTimeout(tabTransitionRef.current);
      }
    };
  }, [activeKey, displayKey]);

  const handleTabChange = (key) => {
    if (!isLandingInfoTabKey(key)) return;

    setActiveKey(key);

    const basePath = isTapRulesPath ? "/about" : location.pathname;
    const params = new URLSearchParams(location.search);

    if (key === "about") {
      params.delete("tab");
    } else {
      params.set("tab", key);
    }

    const search = params.toString();
    navigate(
      {
        pathname: basePath,
        search: search ? `?${search}` : "",
      },
      { replace: false }
    );
  };

  const title = t("seo.title");
  const description = t("seo.description", {
    defaultValue:
      "VaultGrid is a platform that helps everyone live an active social life seamlessly — create and join local events and meet new people.",
  });

  const SITE_URL = window.location.hostname === "ru.vaultgrid.net"
    ? "https://ru.vaultgrid.net"
    : "https://vaultgrid.net";
  const PAGE_URL = `${SITE_URL}/about`;
  const OG_IMAGE_URL = `${SITE_URL}/android-chrome-512x512.png`;
  const pageLang = String(i18n.language || "").toLowerCase().startsWith("ru")
    ? "ru"
    : String(i18n.language || "").toLowerCase().startsWith("sk") ? "sk"
    : String(i18n.language || "").toLowerCase().startsWith("cs") ? "cs" : "en";

  const faqItems = t("faq.items", { returnObjects: true }) || [];
  const faqEntities = Array.isArray(faqItems)
    ? faqItems
        .filter((item) => item && typeof item === "object" && item.q && item.a)
        .map((item) => {
          const answerText = Array.isArray(item.a)
            ? item.a.join("\n")
            : typeof item.a === "string"
              ? item.a
                  .split("•")
                  .map((segment) => segment.trim())
                  .filter(Boolean)
                  .join("\n")
              : String(item.a ?? "");

          return {
            "@type": "Question",
            name: String(item.q),
            acceptedAnswer: {
              "@type": "Answer",
              text: answerText,
            },
          };
        })
    : [];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: "VaultGrid",
        url: SITE_URL,
      },
      {
        "@type": "Organization",
        name: "VaultGrid",
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        email: "mailto:support@vaultgrid.net",
        sameAs: [
          "https://t.me/vaultgrid",
          "https://www.facebook.com/vaultgrid",
          "https://www.instagram.com/vaultgrid_official",
        ],
      },
      ...(faqEntities.length
        ? [
            {
              "@type": "FAQPage",
              mainEntity: faqEntities,
            },
          ]
        : []),
    ],
  };

  return (
    <>
      <Helmet>
        <html lang={pageLang} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index,follow,noimageindex" />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="VaultGrid" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={PAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div
        className="relative min-h-[100dvh] overflow-x-hidden bg-black text-white"
        style={isNative ? { ...AUTH_GRADIENT_STYLE, backgroundColor: "#050812" } : undefined}
      >
        {!isNative ? (
          <>
            <video
              className="fixed inset-0 h-full w-full object-cover"
              src="/assets/videos/hero.faststart.mp4"
              poster="/assets/videos/hero-poster.png"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            />
            <div className="fixed inset-0 bg-[linear-gradient(180deg,rgba(20,40,96,0.78)_0%,rgba(4,10,22,0.92)_42%,rgba(0,0,0,0.98)_100%)]" />
            <div className="fixed inset-0 bg-black/45 backdrop-blur-[2px]" />
          </>
        ) : (
          <div
            className="pointer-events-none fixed inset-0 bg-[linear-gradient(180deg,rgba(5,8,18,0.06)_0%,rgba(5,8,18,0.5)_58%,rgba(5,8,18,0.82)_100%)]"
            aria-hidden="true"
          />
        )}

        {showDesktopLandingHeader ? (
          <div className="hidden md:block">
            <LandingHeader isAuth={false} matchHeroScale />
          </div>
        ) : null}

        <div className={`relative flex min-h-[100dvh] flex-col items-center px-6 pb-36 md:px-10 lg:px-12 ${contentTopPaddingClass}`}>
          {showReturnToRulesSource ? (
            <button
              type="button"
              onClick={handleReturnToRulesSource}
              className="absolute left-4 top-4 z-40 rounded-xl border border-white/25 bg-black/35 px-3 py-2 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-black/45"
            >
              ← {backToRulesSourceLabel}
            </button>
          ) : null}

          <div
            className={`w-full transition-[opacity,transform] duration-200 ${
              isContentVisible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
            }`}
          >
            <LandingInfoContent activeKey={displayKey} variant="page" />
          </div>
        </div>

        <LandingInfoTabs
          activeKey={activeKey}
          onChange={handleTabChange}
          maxWidthClassName="max-w-[336px] md:max-w-[540px]"
          className={`fixed left-1/2 z-40 -translate-x-1/2 ${showDesktopLandingHeader ? "md:hidden" : ""} ${
            showReturnToRulesSource
              ? "top-[5.1rem] md:top-8"
              : showDesktopLandingHeader
                ? "top-[calc(var(--vg-safe-area-top)+30px)] md:top-[148px]"
                : "top-[calc(var(--vg-safe-area-top)+30px)] md:top-8"
          }`}
        />

        <button
          type="button"
          onClick={handleBottomBack}
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+22px)] left-1/2 z-40 flex h-12 w-[280px] -translate-x-1/2 items-center justify-center rounded-full bg-[#3f73ff] px-5 text-sm font-bold text-white shadow-[0_16px_42px_rgba(52,112,255,0.36)] transition hover:bg-[#4a7bff] md:h-14 md:w-[320px] md:text-base"
        >
          {bottomBackLabel}
        </button>
      </div>
    </>
  );
}
